import apiClient from './client';
import type { GL, Vendor, CostCenter, Role, UserType, UserTypeRoleLink, Team, Department } from '../types/admin';
import type { User } from '../types/user';
import { unwrapArray, extractPagination, type PaginationInfo } from '../utils/api-helpers';
import {
  decodeCostCenter,
  decodeDepartment,
  decodeTeam,
  decodeGL,
  decodeVendor,
  decodeRole,
  decodeUserType,
  decodeUser,
  decodeDirectoryUserProfile,
  decodeAdminPullForwardEntry,
  decodeAdminPullForwardPlan,
  decodeSession,
  decodeMaintenanceStatus,
  decodeActivityLogEntry,
  decodeActivityStats,
  decodeErrorLog,
} from '../utils/decodeApiStrings';

// Users (admin list — paginated on server)
export type UsersListSortBy =
  | 'full_name'
  | 'email'
  | 'department'
  | 'is_admin'
  | 'is_active'
  | 'last_login'
  | 'created_at'
  | 'id'
  | 'user_type_name';

export interface GetUsersListParams {
  page: number;
  per_page: number;
  sort_by?: UsersListSortBy;
  sort_dir?: 'asc' | 'desc';
  is_admin?: 'true' | 'false';
  is_active?: 'true' | 'false';
  /** Free-text match on name, email, or department. */
  search?: string;
  /** Comma-separated User Type ids — users assigned any of them. */
  user_type_ids?: string;
  /** 'true' → only users with a User Type or direct role assigned. */
  provisioned?: 'true';
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

/** In-memory list when backend still returns a legacy top-level JSON array (pre-pagination API). */
let legacyUsersFullList: User[] | null = null;

export function invalidateUsersListCache(): void {
  legacyUsersFullList = null;
}

function isPaginatedUsersEnvelope(data: unknown): data is UsersListResponse {
  return (
    data !== null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray((data as UsersListResponse).users) &&
    typeof (data as UsersListResponse).total === 'number'
  );
}

function sliceLegacyUsers(users: User[], params: GetUsersListParams): UsersListResponse {
  const total = users.length;
  const start = (params.page - 1) * params.per_page;
  const pageRows = users.slice(start, start + params.per_page);
  return {
    users: pageRows,
    total,
    page: params.page,
    per_page: params.per_page,
    pages: Math.max(1, Math.ceil(total / params.per_page)),
  };
}

/**
 * Paginated admin users list. Supports both:
 * - Current API: `{ users, total, page, per_page, pages }`
 * - Legacy API: top-level `User[]` (cached after first response; further pages slice in-memory to avoid repeated huge downloads)
 */
export async function getUsersList(params: GetUsersListParams): Promise<UsersListResponse> {
  // Server-side text search / filters can't be honored by the in-memory legacy
  // slice, so always hit the server when any of them are set.
  const hasServerOnlyFilters = Boolean(params.search || params.user_type_ids || params.provisioned);
  if (legacyUsersFullList && !hasServerOnlyFilters) {
    return sliceLegacyUsers(legacyUsersFullList, params);
  }

  const { data } = await apiClient.get<unknown>('/users', { params });

  if (isPaginatedUsersEnvelope(data)) {
    legacyUsersFullList = null;
    return {
      ...data,
      users: data.users.map(decodeUser),
    };
  }

  if (Array.isArray(data)) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        '[api/admin] GET /users returned a legacy JSON array. Restart or rebuild the Flask backend for server-side pagination and smaller payloads.',
      );
    }
    legacyUsersFullList = (data as User[]).map(decodeUser);
    return sliceLegacyUsers(legacyUsersFullList, params);
  }

  throw new Error('Unexpected GET /users response shape (expected paginated object or user array)');
}

/** Load every user page for pickers (admin). Caps page count to avoid runaway requests. */
const MAX_PAGES_ALL_USERS = 100;

export async function getAllUsers(): Promise<User[]> {
  const per_page = 200;
  const r1 = await getUsersList({ page: 1, per_page });
  if (legacyUsersFullList) {
    return [...legacyUsersFullList];
  }
  const all: User[] = [...r1.users];
  let page = 2;
  while (page <= MAX_PAGES_ALL_USERS && page <= r1.pages) {
    const r = await getUsersList({ page, per_page });
    all.push(...r.users);
    page += 1;
  }
  return all;
}

export async function searchUserDirectory(search: string, limit = 30): Promise<User[]> {
  const { data } = await apiClient.get('/users/directory', {
    params: { search, limit },
  });
  return (data as User[]).map(decodeUser);
}

/** Lightweight active-user shape returned by `/users/directory`. */
export interface DirectoryUser {
  id: number;
  full_name: string;
  display_name: string;
  email: string;
  job_title?: string | null;
  is_active: boolean;
}

/**
 * Fetch all active users via the lightweight `/users/directory` endpoint
 * (any authenticated user — no admin role required).
 *
 * Returns the full list in a single request — no pagination — which is fast
 * enough for picker dropdowns even on environments with several thousand
 * users. Use this instead of `getAllUsers()` (admin-paginated `/users`)
 * for non-admin-accessible pickers.
 */
export async function getDirectoryActiveUsers(): Promise<DirectoryUser[]> {
  const { data } = await apiClient.get<DirectoryUser[]>('/users/directory');
  return (data as DirectoryUser[]).map(decodeUser);
}

export async function createUser(user: Partial<User> & { password?: string }): Promise<User> {
  const { data } = await apiClient.post('/users', user);
  invalidateUsersListCache();
  // POST /users responds with { message, user } — unwrap the created user.
  const created = (data && typeof data === 'object' && 'user' in (data as Record<string, unknown>))
    ? (data as { user: User }).user
    : (data as User);
  return decodeUser(created);
}

export async function updateUser(id: number, user: Partial<User> & { password?: string }): Promise<User> {
  const { data } = await apiClient.put(`/users/${id}`, user);
  invalidateUsersListCache();
  return decodeUser(data as User);
}

export async function deleteUser(id: number): Promise<void> {
  await apiClient.delete(`/users/${id}`);
  invalidateUsersListCache();
}

export async function resetPassword(userId: number): Promise<void> {
  await apiClient.post(`/users/${userId}/reset-password`);
}

// GLs
export async function getGLs(options: { includeInactive?: boolean } = {}): Promise<GL[]> {
  const params: Record<string, string> = {};
  if (options.includeInactive) params.include_inactive = 'true';
  const { data } = await apiClient.get('/gls', { params });
  return (data as GL[]).map(decodeGL);
}
export async function createGL(gl: Partial<GL>): Promise<GL> {
  const { data } = await apiClient.post('/gls', gl);
  return decodeGL(data);
}
export async function updateGL(id: number, gl: Partial<GL>): Promise<GL> {
  const { data } = await apiClient.put(`/gls/${id}`, gl);
  return decodeGL(data);
}
export async function deleteGL(id: number): Promise<void> { await apiClient.delete(`/gls/${id}`); }

// Vendors
export async function getVendors(): Promise<Vendor[]> {
  const { data } = await apiClient.get('/vendors');
  return (data as Vendor[]).map(decodeVendor);
}
export async function createVendor(v: Partial<Vendor>): Promise<Vendor> {
  const { data } = await apiClient.post('/vendors', v);
  return decodeVendor(data);
}
export async function updateVendor(id: number, v: Partial<Vendor>): Promise<Vendor> {
  const { data } = await apiClient.put(`/vendors/${id}`, v);
  return decodeVendor(data);
}
export async function deleteVendor(id: number): Promise<void> { await apiClient.delete(`/vendors/${id}`); }

// Cost Centers
export async function getCostCenters(): Promise<CostCenter[]> {
  const { data } = await apiClient.get<CostCenter[]>('/cost-centers');
  return data.map(decodeCostCenter);
}
export async function createCostCenter(cc: Partial<CostCenter>): Promise<CostCenter> {
  const { data } = await apiClient.post('/cost-centers', cc);
  return decodeCostCenter(data);
}
export async function updateCostCenter(id: number, cc: Partial<CostCenter>): Promise<CostCenter> {
  const { data } = await apiClient.put(`/cost-centers/${id}`, cc);
  return decodeCostCenter(data);
}
export async function deleteCostCenter(id: number): Promise<void> { await apiClient.delete(`/cost-centers/${id}`); }

// Roles
export async function getRoles(): Promise<Role[]> {
  const { data } = await apiClient.get('/roles');
  return (data as Role[]).map(decodeRole);
}
export async function createRole(role: Partial<Role>): Promise<Role> {
  const { data } = await apiClient.post('/roles', role);
  return decodeRole(data);
}
export async function updateRole(id: number, role: Partial<Role>): Promise<Role> {
  const { data } = await apiClient.put(`/roles/${id}`, role);
  return decodeRole(data);
}
export async function deleteRole(id: number): Promise<void> { await apiClient.delete(`/roles/${id}`); }
export async function getRoleMembers(id: number): Promise<any[]> { const { data } = await apiClient.get(`/roles/${id}/members`); return data; }
export async function addRoleMember(id: number, userId: number): Promise<void> {
  await apiClient.post(`/roles/${id}/members`, { user_ids: [userId] });
}
export async function removeRoleMember(roleId: number, userId: number): Promise<void> { await apiClient.delete(`/roles/${roleId}/members/${userId}`); }

// User Types
export async function getUserTypes(): Promise<UserType[]> {
  const { data } = await apiClient.get('/user-types');
  return (data as UserType[]).map(decodeUserType);
}
export async function createUserType(ut: Partial<UserType>): Promise<UserType> {
  const { data } = await apiClient.post('/user-types', ut);
  return decodeUserType(data);
}
export async function updateUserType(id: number, ut: Partial<UserType>): Promise<UserType> {
  const { data } = await apiClient.put(`/user-types/${id}`, ut);
  return decodeUserType(data);
}
export async function deleteUserType(id: number): Promise<void> { await apiClient.delete(`/user-types/${id}`); }
export async function getUserTypeMembers(id: number): Promise<any[]> { const { data } = await apiClient.get(`/user-types/${id}/members`); return data; }
export async function addUserTypeMember(id: number, userId: number): Promise<void> { await apiClient.post(`/user-types/${id}/members`, { user_ids: [userId] }); }
export async function removeUserTypeMember(utId: number, userId: number): Promise<void> { await apiClient.delete(`/user-types/${utId}/members/${userId}`); }

export async function getUserTypeRoles(utId: number): Promise<UserTypeRoleLink[]> {
  const { data } = await apiClient.get(`/user-types/${utId}/roles`);
  return data;
}

export async function assignUserTypeRoles(utId: number, roleIds: number[]): Promise<void> {
  await apiClient.post(`/user-types/${utId}/roles`, { role_ids: roleIds });
}

export async function removeUserTypeRole(utId: number, roleId: number): Promise<void> {
  await apiClient.delete(`/user-types/${utId}/roles/${roleId}`);
}

// Teams
export async function getTeams(): Promise<Team[]> {
  const { data } = await apiClient.get<Team[]>('/teams');
  return data.map(decodeTeam);
}
export async function getTeamTree(): Promise<Team[]> {
  const { data } = await apiClient.get<Team[]>('/teams/tree');
  return data.map(decodeTeam);
}
export async function createTeam(team: Partial<Team>): Promise<Team> {
  const { data } = await apiClient.post('/teams', team);
  return decodeTeam(data);
}
export async function updateTeam(id: number, team: Partial<Team>): Promise<Team> {
  const { data } = await apiClient.put(`/teams/${id}`, team);
  return decodeTeam(data);
}
export async function deleteTeam(id: number): Promise<void> { await apiClient.delete(`/teams/${id}`); }

// Departments
export async function getDepartments(): Promise<Department[]> {
  const { data } = await apiClient.get<Department[]>('/departments');
  return data.map(decodeDepartment);
}
export async function createDepartment(dept: Partial<Department>): Promise<Department> {
  const { data } = await apiClient.post('/departments', dept);
  return decodeDepartment(data);
}
export async function updateDepartment(id: number, dept: Partial<Department>): Promise<Department> {
  const { data } = await apiClient.put(`/departments/${id}`, dept);
  return decodeDepartment(data);
}

// Sessions
export interface Session {
  session_id: string;
  user_id: number;
  user_name?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  last_active?: string;
}

export interface MaintenanceStatus {
  enabled: boolean;
  message?: string;
  started_at?: string;
  [key: string]: unknown;
}

export async function getActiveSessions(): Promise<Session[]> {
  const { data } = await apiClient.get('/sessions/active');
  return unwrapArray<Session>(data, 'sessions').map(decodeSession);
}
export async function terminateSession(sessionId: string): Promise<void> { await apiClient.post(`/sessions/terminate/${sessionId}`); }
export async function terminateAllSessions(): Promise<void> { await apiClient.post('/sessions/terminate/all'); }
export async function getMaintenanceStatus(): Promise<MaintenanceStatus> {
  const { data } = await apiClient.get('/sessions/maintenance/status');
  return decodeMaintenanceStatus(data as MaintenanceStatus);
}
export async function updateMaintenance(settings: Partial<MaintenanceStatus>): Promise<void> { await apiClient.post('/sessions/maintenance', settings); }

// Activity Log
export interface ActivityEntry {
  id: number;
  user_id?: number;
  user_name?: string;
  action_type: string;
  entity_type?: string;
  entity_id?: number;
  description?: string;
  details?: Record<string, unknown>;
  ip_address?: string;
  created_at: string;
}

export interface ActivityLogResponse {
  activities: ActivityEntry[];
  pagination: PaginationInfo | null;
}

export interface ActivityStats {
  by_action_type: Record<string, number>;
  by_entity_type: Record<string, number>;
  recent_24h: number;
  recent_7d: number;
  total: number;
  most_active_users: Array<{ user_id: number; user_name: string; count: number }>;
}

/** Backend returns { activities: [...], pagination: {...} } */
export async function getActivityLog(params?: Record<string, string>): Promise<ActivityLogResponse> {
  const { data } = await apiClient.get('/activity-log', { params });
  return {
    activities: unwrapArray<ActivityEntry>(data, 'activities').map(decodeActivityLogEntry),
    pagination: extractPagination(data),
  };
}

export async function getActivityStats(): Promise<ActivityStats> {
  const { data } = await apiClient.get('/activity-log/stats');
  return decodeActivityStats(data as ActivityStats);
}

// Error Logs
export async function getErrorLogs(): Promise<any[]> {
  const { data } = await apiClient.get('/errors');
  const rows = Array.isArray(data) ? data : (data?.errors ?? []);
  return rows.map(decodeErrorLog);
}
export async function deleteError(id: number): Promise<void> { await apiClient.delete(`/errors/${id}`); }
export async function bulkDeleteErrors(ids: number[]): Promise<void> { await apiClient.delete('/errors/bulk', { data: { ids } }); }

// Import Logs
export async function getImportLogs(): Promise<any[]> { const { data } = await apiClient.get('/admin/import-logs'); return data; }
export interface ImportJobsPageResponse {
  items: any[];
  total: number;
  page: number;
  per_page: number;
  active_in_memory?: any[];
}

export async function getImportJobs(params?: {
  page?: number;
  per_page?: number;
  days?: number;
}): Promise<ImportJobsPageResponse> {
  const { data } = await apiClient.get<ImportJobsPageResponse>('/admin/import-logs/jobs', {
    params: {
      universal_import: 1,
      page: params?.page ?? 1,
      per_page: params?.per_page ?? 10,
      ...(params?.days != null ? { days: params.days } : {}),
    },
  });
  return data;
}
export async function getImportJobDetail(jobId: string | number): Promise<any> {
  const { data } = await apiClient.get(`/admin/import-logs/jobs/${jobId}`);
  return data;
}

/** Transaction import profiles (SAP Opex/Capex column maps) for Universal Import. */
export async function getTransactionImportProfiles(): Promise<any[]> {
  const { data } = await apiClient.get('/imports/transaction-import-profiles');
  return Array.isArray(data) ? data : [];
}

// Knowledge
export async function getKnowledge(): Promise<any[]> { const { data } = await apiClient.get('/admin/knowledge'); return data; }
export async function createKnowledge(entry: any): Promise<any> { const { data } = await apiClient.post('/admin/knowledge', entry); return data; }
export async function updateKnowledge(id: number, entry: any): Promise<any> { const { data } = await apiClient.put(`/admin/knowledge/${id}`, entry); return data; }
export async function deleteKnowledge(id: number): Promise<void> { await apiClient.delete(`/admin/knowledge/${id}`); }

// Training Examples
export async function getTrainingExamples(): Promise<any[]> { const { data } = await apiClient.get('/admin/training-examples'); return data; }
export async function createTrainingExample(ex: any): Promise<any> { const { data } = await apiClient.post('/admin/training-examples', ex); return data; }
export async function updateTrainingExample(id: number, ex: any): Promise<any> { const { data } = await apiClient.put(`/admin/training-examples/${id}`, ex); return data; }
export async function deleteTrainingExample(id: number): Promise<void> { await apiClient.delete(`/admin/training-examples/${id}`); }

// Pull Forwards
export async function getPullForwardPlans(): Promise<any[]> {
  const { data } = await apiClient.get('/pull-forward-plans');
  return (data as any[]).map(decodeAdminPullForwardPlan);
}
export async function createPullForwardPlan(plan: any): Promise<any> {
  const { data } = await apiClient.post('/pull-forward-plans', plan);
  return decodeAdminPullForwardPlan(data);
}
export async function updatePullForwardPlan(id: number, plan: any): Promise<any> {
  const { data } = await apiClient.put(`/pull-forward-plans/${id}`, plan);
  return decodeAdminPullForwardPlan(data);
}
export async function deletePullForwardPlan(id: number): Promise<void> { await apiClient.delete(`/pull-forward-plans/${id}`); }
export async function getPullForwardEntries(planId: number): Promise<any[]> {
  const { data } = await apiClient.get(`/pull-forward-plans/${planId}/entries`);
  return (data as any[]).map(decodeAdminPullForwardEntry);
}
export async function createPullForwardEntry(entry: any): Promise<any> {
  const { data } = await apiClient.post('/pull-forward-plans/entries', entry);
  return decodeAdminPullForwardEntry(data);
}
export async function updatePullForwardEntry(id: number, entry: any): Promise<any> {
  const { data } = await apiClient.put(`/pull-forward-plans/entries/${id}`, entry);
  return decodeAdminPullForwardEntry(data);
}
export async function deletePullForwardEntry(id: number): Promise<void> { await apiClient.delete(`/pull-forward-plans/entries/${id}`); }

// Recharge Planning
export async function getRechargeCycles(): Promise<any[]> { const { data } = await apiClient.get('/recharge-planning-cycles'); return data; }

// Email Ingestion
export async function getIngestionRules(): Promise<any[]> { const { data } = await apiClient.get('/admin/email-ingestion/rules'); return data; }
export async function createIngestionRule(rule: any): Promise<any> { const { data } = await apiClient.post('/admin/email-ingestion/rules', rule); return data; }
export async function updateIngestionRule(id: number, rule: any): Promise<any> { const { data } = await apiClient.put(`/admin/email-ingestion/rules/${id}`, rule); return data; }
export async function deleteIngestionRule(id: number): Promise<void> { await apiClient.delete(`/admin/email-ingestion/rules/${id}`); }
export async function runIngestionRule(id: number): Promise<any> { const { data } = await apiClient.post(`/admin/email-ingestion/rules/${id}/run`); return data; }

// Resolver Groups
export async function getResolverGroups(): Promise<any[]> { const { data } = await apiClient.get('/admin/resolver-groups'); return data; }
export async function createResolverGroup(group: any): Promise<any> { const { data } = await apiClient.post('/admin/resolver-groups', group); return data; }
export async function updateResolverGroup(id: number, group: any): Promise<any> { const { data } = await apiClient.put(`/admin/resolver-groups/${id}`, group); return data; }
export async function deleteResolverGroup(id: number): Promise<void> { await apiClient.delete(`/admin/resolver-groups/${id}`); }

// Default Columns
export async function getDefaultColumns(): Promise<any[]> { const { data } = await apiClient.get('/admin/default-columns'); return data; }
export async function updateDefaultColumns(context: string, config: any): Promise<void> { await apiClient.put(`/admin/default-columns/${context}`, config); }

// Directory Users
export interface DirectoryUserProfile {
  id: number;
  full_name: string;
  display_name?: string;
  email?: string;
  job_title?: string;
  department?: string;
  functional_area?: string;
  company_code?: string;
  company_code_text?: string;
  rb_identity?: string;
  employee_number?: string;
  personal_io?: string;
  cost_center?: string;
  profit_center?: string;
  subbrand?: string;
  functional_subarea?: string;
  hire_date?: string;
  technical_account_type?: string;
  nick_name?: string;
  sap_user_id?: string;
  sap_user_cua?: string;
  is_admin: boolean;
  is_active: boolean;
  is_peak_user: boolean;
  is_app_user?: boolean;
  directory_status?: string;
  last_login?: string;
  created_at?: string;
  /** Password lockout — from User.to_dict (admin unlock UX) */
  failed_login_attempts?: number;
  account_locked_until?: string | null;
  is_account_locked?: boolean;
  manager_id?: number;
  manager_name?: string;
  manager_job_title?: string;
  manager_rb_identity?: string;
  highest_recharge_level?: string;
  owned_ios?: Array<{ id: number; order: string; description?: string }>;
  stakeholder_ios?: Array<{ id: number; order: string; description?: string }>;
  assigned_bridges?: Array<{ id: number; name: string }>;
  owned_bridges?: Array<{ id: number; name: string }>;
  direct_reports?: Array<{ id: number; full_name: string; display_name?: string; job_title?: string; technical_account_type?: string; highest_recharge_level?: string }>;
  it_services?: Array<{ service_name: string; workplace_type?: string }>;
  recharge_history?: Array<{ effective_date: string; previous_level?: string; highest_recharge_level?: string }>;
}

export interface DirectoryUsersParams {
  page?: number;
  per_page?: number;
  view?: 'peak' | 'all' | 'directory';
  search?: string;
  directory_status?: string;
}

export interface DirectoryUsersResponse {
  users: DirectoryUserProfile[];
  page: number;
  pages: number;
  total: number;
}

export async function getDirectoryUsers(
  params?: DirectoryUsersParams,
  requestConfig?: { signal?: AbortSignal },
): Promise<DirectoryUsersResponse> {
  const { data } = await apiClient.get<DirectoryUsersResponse>('/directory/users', {
    params,
    signal: requestConfig?.signal,
  });
  return {
    ...data,
    users: (data.users ?? []).map(decodeDirectoryUserProfile),
  };
}

export async function getDirectoryUserProfile(userId: number): Promise<DirectoryUserProfile> {
  const { data } = await apiClient.get(`/directory/users/${userId}`);
  return decodeDirectoryUserProfile(data as DirectoryUserProfile);
}

export async function updateDirectoryUser(userId: number, updates: Partial<Pick<DirectoryUserProfile, 'is_admin' | 'is_active' | 'is_peak_user'>>): Promise<DirectoryUserProfile> {
  const { data } = await apiClient.put(`/directory/users/${userId}`, updates);
  return decodeDirectoryUserProfile(data as DirectoryUserProfile);
}

// Per-user User Types & Roles
export interface UserTypeAssignment {
  user_type_id: number;
  user_type_name: string;
}

export interface UserRoleAssignment {
  role_id: number;
  role_name: string;
  role_key?: string;
  assignment_type?: string;
}

export async function getUserUserTypes(userId: number): Promise<UserTypeAssignment[]> {
  const { data } = await apiClient.get(`/users/${userId}/user-types`);
  return Array.isArray(data) ? data : [];
}

export async function getUserDirectRoles(userId: number): Promise<UserRoleAssignment[]> {
  const { data } = await apiClient.get(`/users/${userId}/roles`);
  return Array.isArray(data) ? data : [];
}

export async function assignUserRole(userId: number, roleId: number): Promise<void> {
  await apiClient.post(`/users/${userId}/roles`, { role_id: roleId });
}

export async function removeUserRole(userId: number, roleId: number): Promise<void> {
  await apiClient.delete(`/users/${userId}/roles/${roleId}`);
}

export async function getUserTypeDetail(utId: number): Promise<any> {
  const { data } = await apiClient.get(`/user-types/${utId}`);
  return data;
}

export async function resetUserPassword(userId: number, newPassword: string): Promise<void> {
  await apiClient.post(`/users/${userId}/reset-password`, { new_password: newPassword });
}

export async function unlockUserAccount(userId: number): Promise<DirectoryUserProfile> {
  const { data } = await apiClient.post<{ message: string; user: DirectoryUserProfile }>(
    `/users/${userId}/unlock-account`,
  );
  return data.user;
}

// IO owner assignment (used from UserProfileSheet)
export async function updateIOOwner(ioId: number, ownerId: number | null): Promise<void> {
  await apiClient.put(`/ios/${ioId}`, { owner_id: ownerId });
}
